import React from 'react';
import { Link } from 'react-router-dom';
import Icon from './Icon';

/**
 * IconButton Component
 *
 * Standard square icon-only action button (share, trash, edit, etc.).
 * Guarantees a 44px touch target on mobile (collapsing to content size on
 * desktop) with the icon centered inside it. The explicit flex centering
 * matters: Tailwind preflight makes SVGs display:block, so a min-sized
 * button without it pins the icon to the top-left on mobile.
 *
 * Props:
 * @param {string} icon - Icon name from the shared Icon registry
 * @param {Function} onClick - Click handler (receives the event)
 * @param {string} title - Tooltip text; also the default aria-label
 * @param {string} ariaLabel - Accessible label (defaults to title)
 * @param {'default'|'danger'} variant - 'danger' for destructive actions (red on hover)
 * @param {boolean} disabled - Disable the button
 * @param {string} className - Additional CSS classes
 * @param {string} iconClassName - Icon size override (default 'w-5 h-5')
 */
const VARIANT_CLASSES = {
  default:
    'text-content-secondary hover:bg-surface-secondary focus-visible:ring-primary-light',
  danger:
    'text-content-secondary hover:bg-error-light hover:text-error hover:border-error focus-visible:ring-error',
};

const IconButton = ({
  icon,
  onClick,
  title,
  ariaLabel,
  variant = 'default',
  disabled = false,
  className = '',
  iconClassName = 'w-5 h-5',
  to = null,
  // Anything else (aria-haspopup, aria-expanded, data-*) passes through —
  // a fixed prop list silently ate callers' ARIA attributes.
  ...rest
}) => {
  const classes = `inline-flex items-center justify-center p-2 min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 rounded-lg border border-border bg-surface transition-all duration-normal active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${VARIANT_CLASSES[variant] || VARIANT_CLASSES.default} ${className}`;
  // `to` renders a real Link in IconButton clothing: a navigation target
  // rendered as a bare <button onClick={navigate}> loses cmd/middle-click,
  // "open in new tab" and its link role — the styling must not cost the
  // anchor semantics.
  if (to) {
    return (
      <Link to={to} className={classes} title={title} aria-label={ariaLabel || title} {...rest}>
        <Icon name={icon} className={iconClassName} />
      </Link>
    );
  }
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={classes}
      title={title}
      aria-label={ariaLabel || title}
      {...rest}
    >
      <Icon name={icon} className={iconClassName} />
    </button>
  );
};

export default React.memo(IconButton);
